import React, { useState, useEffect } from 'react';
import {
  Card, CardContent, Typography, List, ListItem, ListItemText, Divider,
  TextField, Button, Box, Alert, CircularProgress, Stack, Avatar, Grid, Paper, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, IconButton
} from '@mui/material';
import axios from 'axios';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { endpoint } from '../../resource/Constant';

// --- Helper Data ---
const TIME_SLOTS = [
  { start: '08:00', end: '09:00' },
  { start: '09:00', end: '10:00' },
  { start: '10:00', end: '11:00' },
  { start: '11:00', end: '12:00' },
  { start: '12:00', end: '13:00' },
  { start: '13:00', end: '14:00' },
  { start: '14:00', end: '15:00' },
  { start: '15:00', end: '16:00' },
];

const announcements = [
  { title: 'PTM Scheduled', content: 'Parent-Teacher Meeting on 25th May at 10 AM.' },
  { title: 'Annual Sports', content: 'Annual Sports Day on 30th May.' },
];

// Helper to format "HH:mm" or "HH:mm:ss" to "hh:mm AM/PM"
function formatTimeSlot(timeStr) {
  if (!timeStr) return '';
  const [h, m] = timeStr.split(':');
  let hour = parseInt(h, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12 || 12;
  return `${hour}:${m} ${ampm}`;
}

// --- SubjectTable: Shows all classes/subjects for a teacher in a table ---
function SubjectTable({ subjects, teacherId, onDelete }) {
  if (!subjects || subjects.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', opacity: 0.7, mt: 1 }}>
        No subjects assigned
      </Typography>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ boxShadow: 0, mt: 1, mb: 1, borderRadius: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ background: '#e3f2fd' }}>
            <TableCell sx={{ fontWeight: 700, color: '#1976d2' }}>Class</TableCell>
            <TableCell sx={{ fontWeight: 700, color: '#1976d2' }}>Subject</TableCell>
            <TableCell sx={{ fontWeight: 700, color: '#1976d2' }}>Time Slot</TableCell>
            <TableCell sx={{ fontWeight: 700, color: '#1976d2' }}>Delete</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {subjects.map((subj, idx) => (
            <TableRow key={subj.id || `${subj.standard}-${subj.subject}-${idx}`}>
              <TableCell sx={{ fontWeight: 600, color: '#1976d2' }}>
                {subj.standard}
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#0d47a1', textTransform: 'capitalize' }}>
                {subj.subject}
              </TableCell>
              <TableCell sx={{ color: '#666' }}>
                {subj.start_time
                  ? `Time: ${formatTimeSlot(subj.start_time)}${subj.end_time ? ` - ${formatTimeSlot(subj.end_time)}` : ''}`
                  : <span style={{ color: '#bbb' }}>No time slot</span>}
              </TableCell>
              <TableCell>
                <IconButton
                  color="error"
                  size="small"
                  onClick={() => onDelete && onDelete(teacherId, subj.id)}
                >
                  <DeleteIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
// --- TeacherCard: Shows teacher info and their subject table ---
function TeacherCard({ teacher, onAssign, onDelete }) {
  return (
    <Card
      sx={{
        borderRadius: 4,
        boxShadow: 6,
        background: 'linear-gradient(120deg, #e3f2fd 60%, #fce4ec 100%)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        transition: 'box-shadow 0.2s, transform 0.2s',
        '&:hover': { boxShadow: 12, transform: 'scale(1.02)' },
        p: 2,
        m: 1,
      }}
    >
      <CardContent sx={{ pb: '16px !important' }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Avatar
            sx={{
              bgcolor: '#1976d2',
              width: 56,
              height: 56,
              fontWeight: 700,
              fontSize: 26,
              boxShadow: 2,
            }}
          >
            {teacher.full_name?.[0]?.toUpperCase() || <PersonIcon />}
          </Avatar>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: 22, color: '#222' }}>
              {teacher.full_name}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: 15 }}>
              {teacher.email}
            </Typography>
          </Box>
        </Stack>
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>
            Classes & Subjects:
          </Typography>
          <SubjectTable
            subjects={teacher.subjects || []}
            teacherId={teacher.id}
            onDelete={onDelete}
          />
        </Box>
        <Button
          size="small"
          startIcon={<AddIcon />}
          sx={{
            mt: 2,
            bgcolor: '#1976d2',
            color: 'white',
            fontWeight: 600,
            borderRadius: 2,
            px: 3,
            py: 1,
            fontSize: 15,
            boxShadow: 2,
            '&:hover': { bgcolor: '#115293' },
          }}
          variant="contained"
          onClick={() => onAssign(teacher)}
        >
          Assign Subject
        </Button>
      </CardContent>
    </Card>
  );
}

// --- Principal Profile Card ---
function PrincipalProfile() {
  const [profile, setProfile] = useState(null);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    school_name: '',
    tokens_used: 0,
    token_limit: 0,
  });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = localStorage.getItem('school_token');
        const userId = localStorage.getItem('userId');
        const res = await axios.get(
          `${endpoint}/api/getById?id=${userId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setProfile(res.data);
        setForm({
          full_name: res.data.full_name || '',
          email: res.data.email || '',
          school_name: res.data.school_name || '',
          tokens_used: res.data.tokens_used || 0,
          token_limit: res.data.token_limit || 0,
        });
      } catch (err) {
        // Optionally handle error
      }
    };
    fetchProfile();
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('school_token');
      const id = localStorage.getItem('userId');
      await axios.put(
        `${endpoint}/api/update/user/${id}`,
        {
          full_name: form.full_name,
          email: form.email,
          school_name: form.school_name,
          token_limit: form.token_limit,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setEdit(false);
      setProfile({ ...profile, ...form });
    } catch (err) {
      // Optionally handle error
    }
  };

  if (!profile) {
    return (
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: 3 }}>
        <CardContent>
          <Typography variant="h6" color="primary" gutterBottom>
            My Profile
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mb: 3, borderRadius: 3, boxShadow: 3 }}>
      <CardContent>
        <Typography variant="h6" color="primary" gutterBottom>
          My Profile
        </Typography>
        <Stack spacing={2} sx={{ mt: 2 }}>
          <TextField
            label="Full Name"
            name="full_name"
            value={form.full_name}
            onChange={handleChange}
            InputProps={{ readOnly: !edit }}
            fullWidth
            size="small"
          />
          <TextField
            label="Email"
            name="email"
            value={form.email}
            onChange={handleChange}
            InputProps={{ readOnly: !edit }}
            fullWidth
            size="small"
          />
          <TextField
            label="School Name"
            name="school_name"
            value={form.school_name}
            onChange={handleChange}
            InputProps={{ readOnly: !edit }}
            fullWidth
            size="small"
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Tokens Used"
              value={form.tokens_used}
              InputProps={{ readOnly: true }}
              size="small"
              sx={{ flex: 1 }}
            />
            <TextField
              label="Token Limit"
              name="token_limit"
              value={form.token_limit}
              onChange={handleChange}
              InputProps={{ readOnly: !edit ? true : false }}
              size="small"
              sx={{ flex: 1 }}
            />
          </Stack>
        </Stack>
        <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
          {!edit ? (
            <Button variant="outlined" onClick={() => setEdit(true)}>
              Edit
            </Button>
          ) : (
            <>
              <Button variant="contained" onClick={handleSave}>
                Save
              </Button>
              <Button variant="outlined" color="secondary" onClick={() => setEdit(false)}>
                Cancel
              </Button>
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

// --- AssignSubjectDialog ---
function AssignSubjectDialog({
  open, onClose, onSubmit, loading, error, success, form, onFormChange, onTimeSlotChange
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Assign Subject to {form.teacherName}</DialogTitle>
      <DialogContent>
        <TextField
          label="Subject Name"
          name="subject"
          value={form.subject}
          onChange={onFormChange}
          fullWidth
          required
          sx={{ mb: 2, mt: 1 }}
        />
        <TextField
          label="Standard"
          name="standard"
          value={form.standard}
          onChange={onFormChange}
          fullWidth
          required
          sx={{ mb: 2 }}
        />
        <TextField
          select
          label="Time Slot"
          name="timeSlot"
          value={form.start && form.end ? `${form.start}-${form.end}` : ''}
          onChange={onTimeSlotChange}
          fullWidth
          required
          sx={{ mb: 2 }}
          placeholder="Select a time slot"
        >
          {TIME_SLOTS.map((slot) => (
            <MenuItem key={`${slot.start}-${slot.end}`} value={`${slot.start}-${slot.end}`}>
              {formatTimeSlot(slot.start)} - {formatTimeSlot(slot.end)}
            </MenuItem>
          ))}
        </TextField>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 1 }}>{success}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          onClick={onSubmit}
          variant="contained"
          color="primary"
          disabled={loading}
        >
          {loading ? 'Assigning...' : 'Assign'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// --- Main Component ---
const PrincipalDashboard = () => {
  const [form, setForm] = useState({ full_name: '', email: '', password: '' });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [teachers, setTeachers] = useState([]);
  const [fetchingTeachers, setFetchingTeachers] = useState(false);

  // For subject assignment dialog
  const [openAssign, setOpenAssign] = useState(false);
  const [assignTeacher, setAssignTeacher] = useState(null);
  const [subjectForm, setSubjectForm] = useState({
    subject: '',
    standard: '',
    start: '',
    end: '',
    teacherName: '',
  });
  const [subjectLoading, setSubjectLoading] = useState(false);
  const [subjectError, setSubjectError] = useState('');
  const [subjectSuccess, setSubjectSuccess] = useState('');

  // Get principalId and token from localStorage (now stored separately)
  const principalId = localStorage.getItem('userId') || 1;
  const token = localStorage.getItem('school_token');

  // Fetch teachers list (pass role param as 'teacher')
  const fetchTeachers = async () => {
    setFetchingTeachers(true);
    try {
      const res = await axios.get(`${endpoint}/api/admin/getallteachers?role=teacher`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setTeachers(res.data || []);
    } catch (err) {
      setError('Failed to fetch teachers.');
    }
    setFetchingTeachers(false);
  };

  useEffect(() => {
    fetchTeachers();
    // eslint-disable-next-line
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleCreateTeacher = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');
    setLoading(true);

    try {
      const payload = {
        full_name: form.full_name,
        email: form.email,
        password: form.password,
      };
      await axios.post(
        `${endpoint}/api/admin/create/${principalId}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setMessage('Teacher created successfully!');
      setForm({ full_name: '', email: '', password: '' });
      fetchTeachers(); // Refresh the teachers list
    } catch (err) {
      setError('Failed to create teacher.');
    }
    setLoading(false);
  };

  // Subject assignment handlers
  const handleOpenAssignSubjectDialog = (teacher) => {
    setAssignTeacher(teacher);
    setSubjectForm({
      subject: '',
      standard: '',
      start: '',
      end: '',
      teacherName: teacher.full_name,
    });
    setSubjectError('');
    setSubjectSuccess('');
    setOpenAssign(true);
  };

  const handleCloseAssignSubjectDialog = () => {
    setOpenAssign(false);
    setAssignTeacher(null);
    setSubjectForm({ subject: '', standard: '', start: '', end: '', teacherName: '' });
    setSubjectError('');
    setSubjectSuccess('');
  };

  const handleSubjectFormChange = (e) => {
    setSubjectForm({ ...subjectForm, [e.target.name]: e.target.value });
  };

  const handleTimeSlotChange = (e) => {
    const [start, end] = e.target.value.split('-');
    setSubjectForm({ ...subjectForm, start, end });
  };

    // ...existing code...
    const handleAssignSubject = async () => {
      setSubjectLoading(true);
      setSubjectError('');
      setSubjectSuccess('');
      try {
        // Ensure time is in "HH:mm:ss" format
        const toTimeString = (time) => (time && time.length === 5 ? `${time}:00` : time);
  
        await axios.put(
          `${endpoint}/api/admin/assign-subject/${assignTeacher.id}`,
          {
            subject: subjectForm.subject,
            standard: subjectForm.standard,
            start_time: toTimeString(subjectForm.start),
            end_time: toTimeString(subjectForm.end),
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setSubjectSuccess('Subject assigned successfully!');
        fetchTeachers();
        setTimeout(() => {
          handleCloseAssignSubjectDialog();
        }, 1200);
      } catch (err) {
        // Show backend error detail if available
        setSubjectError(
          err?.response?.data?.detail ||
          err?.response?.data?.message ||
          'Failed to assign subject. Please check the details.'
        );
      }
      setSubjectLoading(false);
    };
  // ...existing code...
  // Delete assigned subject handler
  const handleDeleteAssignedSubject = async (teacherId, subjectId) => {
    try {
      await axios.delete(
        `${endpoint}/api/admin/delete/assignedclass/${teacherId}?subId=${subjectId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      fetchTeachers();
    } catch (err) {
      setError('Failed to delete assigned subject.');
    }
  };

  return (
    <Box sx={{ width: '100%', px: { xs: 1, sm: 2, md: 4 }, py: 3 }}>
      <Grid container spacing={3}>
        {/* My Profile - always full width */}
        <Grid item xs={12}>
          <PrincipalProfile />
        </Grid>

        {/* Create Teacher & Announcements side by side on md+, stacked on xs/sm */}
        <Grid item xs={12} md={6}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" color="primary" gutterBottom>
                Create Teacher
              </Typography>
              <Box component="form" onSubmit={handleCreateTeacher} sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="Full Name"
                  name="full_name"
                  value={form.full_name}
                  onChange={handleChange}
                  required
                  size="small"
                />
                <TextField
                  label="Email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  size="small"
                />
                <TextField
                  label="Password"
                  name="password"
                  type="password"
                  value={form.password}
                  onChange={handleChange}
                  required
                  size="small"
                />
                <Button type="submit" variant="contained" color="primary" disabled={loading}>
                  {loading ? 'Creating...' : 'Create'}
                </Button>
              </Box>
              {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" color="primary" gutterBottom>
                Announcements
              </Typography>
              <List>
                {announcements.map((a, idx) => (
                  <React.Fragment key={a.title}>
                    <ListItem>
                      <ListItemText
                        primary={a.title}
                        secondary={a.content}
                      />
                    </ListItem>
                    {idx < announcements.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Teachers List - always full width */}
        <Grid item xs={12}>
          <Paper
            elevation={6}
            sx={{
              p: { xs: 2, sm: 3, md: 4 },
              borderRadius: 4,
              background: 'linear-gradient(120deg, #f5faff 60%, #fce4ec 100%)',
              boxShadow: 8,
              mt: 2,
            }}
          >
            <Typography variant="h6" color="primary" gutterBottom sx={{ fontWeight: 700, fontSize: 22, mb: 3 }}>
              Teachers List
            </Typography>
            {fetchingTeachers ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                <CircularProgress />
              </Box>
            ) : teachers.length === 0 ? (
              <Typography color="text.secondary" sx={{ textAlign: 'center', my: 2 }}>
                No teachers found.
              </Typography>
            ) : (
              <Grid container spacing={3}>
                {teachers.map((teacher) => (
                  <Grid item xs={12} key={teacher.id || teacher.email}>
                    <TeacherCard
                      teacher={teacher}
                      onAssign={handleOpenAssignSubjectDialog}
                      onDelete={handleDeleteAssignedSubject}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </Paper>
        </Grid>
      </Grid>

      <AssignSubjectDialog
        open={openAssign}
        onClose={handleCloseAssignSubjectDialog}
        onSubmit={handleAssignSubject}
        loading={subjectLoading}
        error={subjectError}
        success={subjectSuccess}
        form={subjectForm}
        onFormChange={handleSubjectFormChange}
        onTimeSlotChange={handleTimeSlotChange}
      />
    </Box>
  );
};

export default PrincipalDashboard;